import { Project } from '@/types/project';
import { getDay } from 'date-fns';

import { Admin } from '@/types/admin';
import { Contact, contactFactory } from '@/types/contact';
import { ProjectClass } from '@/types/projectClass';
import { Service } from '@/types/service';
import { SubscriptionRequest } from './request';
import { SERVICE_GROUP_ID } from '@/constants';

import { URL_CATEGORIES } from '@/types/serviceGroup';
import { User } from '@/types/user';
import { BUSINESS_DAYS_LATERS } from '@/constants';

export type Subscription = {
  id: number;
  requestCode: string;
  type: string;
  isUpdatable: boolean;
  appliedAt: string;
  userCount: number;
  services: Service[];
  startsOn: string[];
  project?: Project;
  projectClass?: ProjectClass;
  billingNote?: string;
};

export type SubscriptionDetail = {
  admins: Admin[];
  users: User[];
  billingContact: Contact;
  contact: Contact;
} & Subscription;

const defaultSubscription: SubscriptionDetail = {
  id: 0,
  requestCode: '',
  type: '',
  isUpdatable: false,
  appliedAt: '',
  userCount: 0,
  services: [],
  admins: [],
  startsOn: [],
  users: [],
  billingContact: contactFactory(),
  contact: contactFactory(),
};

export const subscriptionFactory = (subscriptionAttrs?: {
  [P in keyof SubscriptionDetail]?: SubscriptionDetail[P];
}): SubscriptionDetail => ({ ...defaultSubscription, ...subscriptionAttrs });

export const castToRequest = (
  subscription: SubscriptionDetail,
): SubscriptionRequest => {
  const payload: SubscriptionRequest = {
    adminIds: subscription.admins.map((a) => a.id),
    serviceIds: subscription.services.map((s) => s.id),
    startsOn: subscription.startsOn[0],
    users: subscription.users.map((user, key) => ({ ...user, key })),
    billingNote: subscription.billingNote,
  };
  if (subscription.projectClass) {
    payload.projectClassId = subscription.projectClass.id;
  } else {
    payload.billingContactId = subscription.billingContact.id;
    payload.contactId = subscription.contact.id;
  }

  return payload;
};

/**
 * 対象の日付から見た、直近の1営業日の次の日を返す
 * @param date - 対象の日付
 */
export const passBusinessDays = (date: number | Date = new Date()) => {
  return BUSINESS_DAYS_LATERS[getDay(date)];
};

export const pickServiceGroupId = (subscription: Subscription) => {
  const [service] = subscription.services;
  if (!service || !service.serviceGroup) {
    return null;
  }
  return service.serviceGroup.id;
};

export const getTermsUrls = (subscription: Subscription) => {
  const [service] = subscription.services;
  if (!service || !service.serviceGroup) {
    return [];
  }
  return service.serviceGroup.urls.filter(
    (entry) => entry.category === URL_CATEGORIES.TERMS,
  );
};

export const pickServiceGroupName = (subscription: Subscription) => {
  const [service] = subscription.services;
  if (!service || !service.serviceGroup) {
    return '';
  }
  return service.serviceGroup.name;
};

// 消せる
export const pickProjectName = (subscription: Subscription) => {
  const { projectClass } = subscription;
  if (!projectClass) {
    return '';
  }
  return projectClass.name;
};

// [TODO] このファイルは型定義に関するものなので、
//        isEMBAとisGLSは適切な場所に移動する
export const isEMBA = (serviceGroupId: number) => {
  return serviceGroupId === SERVICE_GROUP_ID.emba;
};

export const isGLS = (serviceGroupId: number) => {
  return serviceGroupId === SERVICE_GROUP_ID.gls;
};
